
import React from 'react';
import { FileText, Download, AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Document } from './types';
import { useDocumentDownload } from './useDocumentDownload';
import { useIsMobile } from '@/hooks/use-mobile';

interface DocumentListProps {
  documents: Document[];
}

const DocumentList = ({ documents }: DocumentListProps) => {
  const { handleDownload } = useDocumentDownload();
  const isMobile = useIsMobile();

  if (documents?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No hay documentos disponibles</h3>
        <p className="text-muted-foreground">
          Los documentos se mostrarán aquí cuando estén disponibles.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Warning Banner */}
      {documents?.some(doc => doc.fileAvailable === false) && (
        <div className="mb-6 p-4 bg-slate-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-coolGray flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
            Algunos documentos no están disponibles temporalmente para descarga.
          </p>
        </div>
      )}
      
      {/* Document Cards */}
      <div className="space-y-5">
        {documents?.map((doc) => (
          <div 
            key={doc.id} 
            className="flex items-center gap-4 p-5 border border-gray-100 rounded-xl hover:border-gray-200 hover:shadow-sm transition-all duration-200 bg-white"
          >
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-primary" />
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-navy text-base md:text-lg">
                  {doc.title}
                </h3>
                <Badge 
                  variant={doc.fileAvailable === false ? "secondary" : "default"}
                  className={doc.fileAvailable === false ? "bg-gray-100 text-gray-600" : "bg-emerald-100 text-emerald-700"}
                >
                  {doc.fileAvailable === false ? 'No disponible' : 'Disponible'}
                </Badge>
              </div>
              {doc.description && (
                <p className="text-sm text-coolGray leading-relaxed">
                  {doc.description}
                </p>
              )}
            </div>
            
            {/* Download Button */}
            <div className="flex-shrink-0">
              <Button
                variant={doc.fileAvailable === false ? "outline" : "default"}
                size="sm"
                onClick={() => handleDownload(doc.filename, doc.title)}
                disabled={doc.fileAvailable === false}
                className={`
                  ${doc.fileAvailable === false 
                    ? 'border-gray-300 text-gray-500 hover:bg-gray-50' 
                    : 'bg-primary hover:bg-primary/90 text-white'
                  }
                  ${isMobile ? 'min-h-[44px] px-4' : 'px-4 py-2'}
                  transition-all duration-200
                `}
              >
                {doc.fileAvailable === false ? (
                  <AlertTriangle className="h-4 w-4 mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {doc.fileAvailable === false ? 'No disponible' : 'Descargar'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentList;
